// Arlo's firsts: a card in the diary, and a little celebration (with
// confetti) on each device the first time it sees a new one.

import type { LucideIcon } from 'lucide-react';
import { Cake, Camera, Droplet, Heart, House, Moon, PawPrint, Sparkles, Sun, Trophy } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDevice, useEvents, useHousehold, useNow, useProfile } from '../../data/hooks.ts';
import { ageNow } from '../../domain/age.ts';
import { firsts, freshFirsts, upcomingFirsts, validArrival, type First, type FirstIcon } from '../../domain/firsts.ts';
import { formatDate, inClockWindow } from '../../domain/time.ts';
import { navigate } from '../../app/router.ts';
import { Button, Card, CardHeader } from '../../ui/primitives.tsx';

export const FIRST_ICONS: Record<FirstIcon, LucideIcon> = { home: House, drop: Droplet, sun: Sun, moon: Moon, paw: PawPrint, star: Sparkles, heart: Heart, trophy: Trophy, cake: Cake };

export function useFirsts(): First[] {
  const events = useEvents();
  const profile = useProfile();
  const household = useHousehold();
  const now = useNow(60_000);
  // Recomputed when something's logged, or once a minute for the date-based ones.
  const minute = Math.floor(now / 60_000);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `minute` is the clock tick; read the real time so a first logged seconds ago counts.
  return useMemo(() => firsts(events, profile.homeDate, household, Date.now(), validArrival(profile.homeDate, profile.arrivedAt)), [events, profile.homeDate, profile.arrivedAt, household, minute]);
}

// --- The diary card ---------------------------------------------------------------------

export function FirstsCard() {
  const reached = useFirsts();
  const profile = useProfile();
  const next = upcomingFirsts(reached, 3);
  const latestFirst = [...reached].reverse();
  return (
    <Card className="firsts-card">
      <CardHeader
        title="His firsts"
        icon={Trophy}
        sub={reached.length ? `${reached.length} so far — spotted from what you log.` : `They’ll collect here from ${profile.homeDate ? formatDate(profile.homeDate, 'long') : 'the day he comes home'}, spotted from what you log.`}
      />
      {latestFirst.length > 0 && (
        <ol className="firsts">
          {latestFirst.map((f) => {
            const Icon = FIRST_ICONS[f.icon];
            const age = ageNow(profile, f.at)?.label;
            return (
              <li key={f.id} className="first">
                <span className="first-icon">
                  <Icon aria-hidden />
                </span>
                <span className="first-text">
                  <strong>{f.title}</strong>
                  <span className="muted small">
                    {formatDate(f.at, 'weekday')}
                    {age ? ` · ${age}` : ''}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {next.length > 0 && (
        <>
          <h3 className="eyebrow firsts-next-title">Still to come</h3>
          <ul className="firsts firsts-next">
            {next.map((f) => {
              const Icon = FIRST_ICONS[f.icon];
              return (
                <li key={f.id} className="first is-next">
                  <span className="first-icon">
                    <Icon aria-hidden />
                  </span>
                  <span className="first-text">
                    <strong>{f.title}</strong>
                    <span className="muted small">{f.hint}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
}

// --- The celebration -------------------------------------------------------------------------

const KEY = 'arlo:firsts';

function loadSeen(): { since: number; seen: string[] } {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const v = JSON.parse(raw) as { since?: number; seen?: string[] };
      if (typeof v.since === 'number') return { since: v.since, seen: Array.isArray(v.seen) ? v.seen : [] };
    }
  } catch {
    /* storage unavailable */
  }
  // First time on this device: start watching from now.
  const fresh = { since: Date.now(), seen: [] };
  saveSeen(fresh);
  return fresh;
}

function saveSeen(v: { since: number; seen: string[] }) {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    /* storage unavailable */
  }
}

export function FirstsHost() {
  const reached = useFirsts();
  const profile = useProfile();
  const household = useHousehold();
  const now = useNow(60_000);
  const [state, setState] = useState(loadSeen);
  const arrived = validArrival(profile.homeDate, profile.arrivedAt) !== undefined;
  // "Home for good" waits for the "He's home!" tap, not midnight on a dark tablet.
  const fresh = useMemo(() => freshFirsts(reached, new Set(state.seen), state.since, Math.max(now, Date.now())).filter((f) => f.id !== 'home' || arrived), [reached, state, now, arrived]);
  // No confetti at 2am: anything reached overnight (like "One week home" at midnight) waits for the morning.
  const night = inClockWindow(now, household.nightStart, household.nightEnd);

  const done = () => {
    const next = { ...state, seen: [...state.seen, ...fresh.map((f) => f.id)].slice(-200) };
    saveSeen(next);
    setState(next);
  };

  if (!fresh.length || night) return null;
  // Several at once (a busy day, or a phone opened the next morning): one card, the latest up top.
  const [first, ...also] = [...fresh].reverse();
  return <Celebration key={first.id} first={first} also={also} onDone={done} />;
}

const CONFETTI_COLOURS = ['#e07b3f', '#f2b134', '#5aa469', '#4f86c6', '#d36b9a', '#9b6fd1'];

function Celebration({ first, also, onDone }: { first: First; also: First[]; onDone: () => void }) {
  const profile = useProfile();
  const kiosk = !!useDevice().kiosk;
  const Icon = FIRST_ICONS[first.icon];
  const age = ageNow(profile, first.at)?.label;
  // On the wall tablet it bows out by itself after a while. (A ref, so the
  // parent's once-a-minute re-render doesn't restart the timer.)
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    if (!kiosk) return;
    const t = setTimeout(() => doneRef.current(), 10 * 60_000);
    return () => clearTimeout(t);
  }, [kiosk]);
  const pieces = useMemo(
    () =>
      Array.from({ length: 48 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        duration: 2.6 + Math.random() * 2,
        rotate: Math.random() * 360,
        colour: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
        wide: i % 3 === 0,
      })),
    [],
  );
  return (
    <div className="celebrate" role="dialog" aria-modal="true" aria-labelledby="celebrate-title" onClick={onDone}>
      <div className="confetti" aria-hidden>
        {pieces.map((p, i) => (
          <i key={i} style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`, background: p.colour, transform: `rotate(${p.rotate}deg)`, width: p.wide ? 12 : 7 }} />
        ))}
      </div>
      <div className="celebrate-card" onClick={(e) => e.stopPropagation()}>
        <span className="celebrate-icon">
          <Icon aria-hidden />
        </span>
        <p className="eyebrow">{first.id === 'home' ? 'The big day' : `A first for ${profile.name || 'Arlo'}`}</p>
        <h2 id="celebrate-title">{first.id === 'home' ? `Welcome home, ${profile.name || 'Arlo'}!` : first.title}</h2>
        <p>{first.cheer}</p>
        <p className="muted small">
          {formatDate(first.at, 'long')}
          {age ? ` · ${age} old` : ''}
        </p>
        {also.length > 0 && (
          <div className="celebrate-also">
            <p className="eyebrow">And {also.length === 1 ? 'another first' : `${also.length} more firsts`}</p>
            <ul>
              {also.map((f) => {
                const I = FIRST_ICONS[f.icon];
                return (
                  <li key={f.id}>
                    <I aria-hidden /> {f.title}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div className="row celebrate-actions">
          <Button
            icon={Camera}
            onClick={() => {
              onDone();
              navigate('/diary');
            }}
          >
            Add a photo
          </Button>
          <Button variant="primary" onClick={onDone} autoFocus>
            Lovely!
          </Button>
        </div>
      </div>
    </div>
  );
}
